import dayjs from 'dayjs';
import { usePatient } from 'hooks/api/useUser';
import { Timeframes } from 'models/filter';
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { calculateStartTimeFrame, getEnlistmentDateRange, getEnlistmentKey } from 'utils/dates';
import { getUserMeasurementSystem } from 'utils/helpers';
import { UnitType } from 'models/generic';
import { Enlistment } from 'models/user';
import { useParameters } from 'hooks/useParameters';
import { queryParams } from 'utils/query';
import { useAppContext } from './hooks';

interface PatientDetailContextType {
  startDate: Date;
  endDate: Date;
  setStartDate: Dispatch<SetStateAction<Date>>;
  setEndDate: Dispatch<SetStateAction<Date>>;
  timeframe: Timeframes;
  changeTimeFrame: (value: Timeframes) => void;
  symptoms: string[];
  setSymptoms: Dispatch<SetStateAction<string[]>>;
  unitSystem: UnitType;
  setUnitSystem: Dispatch<SetStateAction<UnitType>>;
  prescriptions: Enlistment[];
  selectedPrescriptionKey?: string;
  selectPrescription: (key: string) => void;
  clearPrescriptionSelection: () => void;
}

const PatientDetailContext = createContext<PatientDetailContextType | undefined>(undefined);
PatientDetailContext.displayName = 'PatientDetail';

const PatientDetailProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useAppContext();
  const { patientId } = useParams<{ patientId: string }>();
  const { data: patientData } = usePatient({ patientId });
  const { parameters, setParameters } = useParameters();
  const datesToStart = calculateStartTimeFrame({
    enlistments: patientData?.visibleEnlistments,
    measurementTimestamp: Number(parameters[queryParams.timestamp]),
    clearParameter: () => setParameters({ ...parameters, [queryParams.timestamp]: '' }),
  });
  const [startDate, setStartDate] = useState<Date>(datesToStart.startDate);
  const [endDate, setEndDate] = useState<Date>(datesToStart.endDate);
  const [timeframe, setTimeframe] = useState<Timeframes>(Timeframes.Custom);
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [unitSystem, setUnitSystem] = useState<UnitType>(getUserMeasurementSystem(currentUser));

  const prescriptions = patientData?.visibleEnlistments || [];
  const [selectedPrescriptionKey, setSelectedPrescriptionKey] = useState<string | undefined>(
    prescriptions[0] ? getEnlistmentKey(prescriptions[0]) : undefined,
  );

  const selectPrescription = (key: string) => {
    const prescription = prescriptions.find((enlistment) => getEnlistmentKey(enlistment) === key);
    if (!prescription) return;

    const { startDate: prescriptionStartDate, endDate: prescriptionEndDate } = getEnlistmentDateRange(prescription);
    setSelectedPrescriptionKey(key);
    setStartDate(prescriptionStartDate);
    setEndDate(prescriptionEndDate);
  };

  const clearPrescriptionSelection = () => setSelectedPrescriptionKey(undefined);

  const changeTimeFrame = (value: Timeframes) => {
    const today = dayjs().endOf('day');
    const startOfDay = dayjs(today).startOf('day');

    setTimeframe(value);

    if (value === Timeframes.Custom) return;
    clearPrescriptionSelection();
    setEndDate(dayjs(today).toDate());
    switch (value) {
      case Timeframes.lastWeek: {
        setStartDate(startOfDay.subtract(1, 'week').toDate());
        break;
      }
      case Timeframes.lastMonth: {
        setStartDate(startOfDay.subtract(1, 'month').toDate());
        break;
      }
      case Timeframes.lastThreeMonths: {
        setStartDate(startOfDay.subtract(3, 'month').toDate());
        break;
      }
      case Timeframes.lastYear: {
        setStartDate(startOfDay.subtract(1, 'year').toDate());
        break;
      }
      default: {
        setStartDate(startOfDay.subtract(1, 'month').toDate());
        break;
      }
    }
  };

  return (
    <PatientDetailContext.Provider
      value={{
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        timeframe,
        changeTimeFrame,
        symptoms,
        setSymptoms,
        unitSystem,
        setUnitSystem,
        prescriptions,
        selectedPrescriptionKey,
        selectPrescription,
        clearPrescriptionSelection,
      }}
    >
      {children}
    </PatientDetailContext.Provider>
  );
};

export { PatientDetailContext, PatientDetailProvider };
